import React, { useCallback, useEffect } from 'react'

import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { parseMetadata } from '../../../apps/professional-app/fa/MeetingSummary/utils'

import { NewEmailResult } from '../../../models/NewEmail'
import { AIAgentType } from '../../../models/AIAgent'

import { selectShouldPollNewEmailCitations, setNewEmailCitationLoading, setNewEmailCitations, setNewEmailResult } from '../../../store/emailHub/newEmail'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { isNewEmailNoResult } from '../NewEmail/utils'

export const NewEmailPoller: React.FC = () => {
  usePollNewEmailCitations()

  return null
}

const usePollNewEmailCitations = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const shouldPollCitations = useAppSelector(selectShouldPollNewEmailCitations)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const emailConnector = emailIntegration?.serviceId ?? null

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const getCitations = useCallback(async () => {
    if (!emailConnector) {
      return
    }
    const { data: aiAgentResult } = await triggerGetAiAgentResult({
      accountId,
      type: AIAgentType.NEW_EMAIL,
      resultKey: `newEmail|${currentUserId}|${emailConnector}`,
    })
    if (!aiAgentResult) {
      return
    }
    const { result, metadata } = aiAgentResult
    const { citations, emailSignatureProcessed, startedGenerating } = parseMetadata(metadata)
    if (!citations) {
      if (startedGenerating) {
        dispatch(setNewEmailCitationLoading(true))
      }
      return
    }
    if (!isNewEmailNoResult(result as NewEmailResult) && !emailSignatureProcessed) {
      return
    }
    dispatch(setNewEmailCitations(citations))
    dispatch(setNewEmailResult(result as NewEmailResult))
    dispatch(setNewEmailCitationLoading(false))
  }, [dispatch, accountId, currentUserId, emailConnector, triggerGetAiAgentResult])

  const { start: startNewEmailCitationPolling, stop: stopNewEmailCitationPolling } = usePollingEffect(getCitations, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (shouldPollCitations) {
      startNewEmailCitationPolling()
    } else {
      stopNewEmailCitationPolling()
      dispatch(setNewEmailCitationLoading(false))
    }
  }, [dispatch, shouldPollCitations, startNewEmailCitationPolling, stopNewEmailCitationPolling])
}
